import React, { useEffect, useReducer, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Share2, Copy, Check, ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import { articlesAPI, absUrl } from "../services/api";
import { CATEGORY_MAP } from "../lib/categories";
import ArticleCard from "../components/ArticleCard";
import Seo from "../components/Seo";
import "./ArticleDetail.css";

const initialDetail = { article: null, related: [], loading: true, notFound: false };
function detailReducer(state, action) {
  switch (action.type) {
    case "loading": return { ...initialDetail };
    case "ok": return { article: action.article, related: action.related, loading: false, notFound: false };
    case "notfound": return { article: null, related: [], loading: false, notFound: true };
    case "err": return { ...state, loading: false };
    default: return state;
  }
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(detailReducer, initialDetail);
  const { article, related, loading, notFound } = state;
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dispatch({ type: "loading" });
    articlesAPI
      .getBySlug(slug)
      .then((r) => {
        if (cancelled) return;
        dispatch({ type: "ok", article: r.data.article, related: r.data.related || [] });
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 404) dispatch({ type: "notfound" });
        else dispatch({ type: "err" });
      });
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return <div className="article-detail"><div className="loading-container"><div className="loading-spinner" /></div></div>;
  }

  if (notFound || !article) {
    return (
      <div className="article-detail">
        <Seo title="Article not found" noIndex />
        <div className="container" style={{ padding: "120px 0", textAlign: "center" }}>
          <h1 className="heading-2">404 — Article not found</h1>
          <p className="body-medium" style={{ marginTop: 16 }}>This story may have been removed or moved.</p>
          <Link to="/news" className="btn-primary" style={{ marginTop: 24 }}>Back to News</Link>
        </div>
      </div>
    );
  }

  const url = typeof window !== "undefined" ? window.location.href : "";
  const categoryName = CATEGORY_MAP[article.category] || article.category;
  const dateObj = article.published_at ? new Date(article.published_at) : null;
  const dateLabel = dateObj
    ? dateObj.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.meta_description || article.excerpt,
    image: article.featured_image ? [absUrl(article.featured_image)] : undefined,
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    author: { "@type": "Person", name: article.author },
    publisher: {
      "@type": "Organization",
      name: "Race377",
      logo: { "@type": "ImageObject", url: `${process.env.REACT_APP_BACKEND_URL}/logo192.png` },
    },
    articleSection: categoryName,
    keywords: (article.tags || []).join(", "),
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* noop */ }
  };

  const tryNativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: article.title, text: article.excerpt, url }); }
      catch (e) { /* user cancel */ }
    } else {
      copyLink();
    }
  };

  return (
    <article className="article-detail" data-testid="article-detail-page">
      <Seo
        title={article.meta_title || article.title}
        description={article.meta_description || article.excerpt}
        image={article.featured_image ? absUrl(article.featured_image) : undefined}
        type="article"
        jsonLd={jsonLd}
      />

      <div className="container article-detail__back">
        <button onClick={() => navigate(-1)} className="article-detail__back-btn" data-testid="article-back-btn">
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      <header className="article-detail__header">
        <div className="container">
          <Link to={`/categories/${article.category}`} className="article-detail__category" data-testid="article-category-link">
            {categoryName}
          </Link>
          <h1 className="article-detail__title">{article.title}</h1>
          <p className="article-detail__excerpt">{article.excerpt}</p>
          <div className="article-detail__meta">
            <span><strong>{article.author}</strong></span>
            {dateLabel && <><span className="dot">•</span><span>{dateLabel}</span></>}
          </div>
        </div>
      </header>

      {article.featured_image && (
        <figure className="article-detail__hero">
          <img src={absUrl(article.featured_image)} alt={article.title} loading="eager" />
        </figure>
      )}

      <div className="container article-detail__body-wrap">
        <div
          className="article-detail__body prose"
          data-testid="article-body"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body) }}
        />

        <div className="article-detail__share">
          <span className="caption">Share this story</span>
          <div className="article-detail__share-actions">
            <button className="share-btn" onClick={tryNativeShare} data-testid="article-share-btn">
              <Share2 size={16} /> Share
            </button>
            <button className="share-btn" onClick={copyLink} data-testid="article-copy-link-btn">
              {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>

        {(article.tags || []).length > 0 && (
          <div className="article-detail__tags">
            {article.tags.map((t) => (
              <span key={t} className="article-tag">#{t}</span>
            ))}
          </div>
        )}
      </div>

      {related.length > 0 && (
        <section className="article-related">
          <div className="container">
            <h2 className="heading-2" style={{ marginBottom: 32 }}>Related stories</h2>
            <div className="article-related__grid">
              {related.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
